/* eslint-disable no-sequences */
import { ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AddToCartButton from '../../../../../../common/add-to-cart-button/add-to-cart-button';
import Spinner from '../../../../../../ui/spinner/spinner';
import { SPINNER_SIZES } from '../../../../../../ui/spinner/spinner.constants';
import { BUTTON_VARIANTS } from '../../../../../../constants/button-variants';
import useToast from '../../../../../../hooks/use-toast-notification';
import translation from '../../../../../../languages';
import refetchCart from '../../../../../../services/cart/refetch-cart';
import useRemoveDuplicatedFromCart from '../../../../../../services/cart/use-remove-duplicated-from-cart';
import BundleMaterialCard from '../../../components/two-column-desc-bund/components/bundle-material-card/bundle-material-card';
import './bundle-material-card-with-add-to-cart.scss';

type BundleMaterialCardWithAddToCartProps = TranslationProps & {
    material: Material;
    materialIndex: number;
    idBeingAdded: number | null;
    updateIdBeingAdded: (newId: number | null) => void;
};

const BundleMaterialCardWithAddToCart = (props: BundleMaterialCardWithAddToCartProps): ReactElement => {
    const { material, materialIndex, idBeingAdded, updateIdBeingAdded, t } = props;

    const isAddingAnyBundleToCart = Boolean(idBeingAdded);
    const isAddingCurrentToCart = isAddingAnyBundleToCart && idBeingAdded === material.id;

    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { mutateAsync: removeDuplicatedFromCart } = useRemoveDuplicatedFromCart();

    const showSuccessToast = () => showToast('success', t('cart:cart.notifications.bundleAdded'), 'interactive');
    const showErrorToast = () => showToast('error', t('login:login.forgotPassword.serverError'), 'interactive');

    const onAddBundleStart = (): boolean => (!idBeingAdded && updateIdBeingAdded(material.id), true);
    const onAddBundleFinish = (): void => updateIdBeingAdded(null);
    const onAddBundleError = (): void => (showErrorToast(), onAddBundleFinish());

    const onDeleteDuplicatesFinish = (): void => (showSuccessToast(), onAddBundleFinish());
    // the add response was not synced into the cache, so restore the cart from the server; the yellow duplicates warning stays visible
    const onDeleteDuplicatesError = (): Promise<Cart | void> => refetchCart(queryClient).catch(() => {});
    const onDeleteDuplicatesStart = (): Promise<Cart | void> =>
        removeDuplicatedFromCart().catch(onDeleteDuplicatesError).finally(onDeleteDuplicatesFinish);

    if (isAddingCurrentToCart) {
        return (
            <BundleMaterialCard material={material} className='with-spinner'>
                <Spinner size={SPINNER_SIZES.SMALL} />
            </BundleMaterialCard>
        );
    }

    return (
        <BundleMaterialCard material={material} className='with-add-to-cart'>
            <AddToCartButton
                material={material}
                materialIndex={materialIndex}
                onAddToCartCallback={onAddBundleStart}
                onAddToCartSuccess={onDeleteDuplicatesStart}
                onAddToCartError={onAddBundleError}
                className={BUTTON_VARIANTS.SECONDARY}
                size='small'
                isDisabled={isAddingAnyBundleToCart}
                shouldPreventCartOpen
                hideRestrictionsMessage
                shouldSyncCartOnSuccess={false}
            />
        </BundleMaterialCard>
    );
};

const translate = translation(['login', 'cart']);

export default translate(BundleMaterialCardWithAddToCart);
